import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Webhook, Copy, RefreshCw, Plus, Power, ChevronRight, AlertCircle, Code2,
} from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT",
  { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const copy = (v, label) => {
  navigator.clipboard?.writeText(v || "");
  toast.success(`${label} copiato`);
};

const SOURCE_TYPES = [
  { v: "meta_landing", l: "Landing Meta Ads" },
  { v: "web_landing", l: "Landing sito" },
  { v: "external_form", l: "Form esterno" },
];

export default function AgencyLeadWebhooks() {
  const [sources, setSources] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(null);
  const [events, setEvents] = useState([]);
  const [contract, setContract] = useState(null);
  const [form, setForm] = useState({
    tenant_id: "", name: "", source_type: "meta_landing",
    acquisition_source: "meta_ads", landing_name_default: "", form_name_default: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/agency/lead-webhooks");
      setSources(r.data.sources || []);
    } catch { toast.error("Impossibile caricare le sorgenti"); }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    api.get("/admin/tenants").then((r) => setTenants(r.data?.tenants || r.data || []))
      .catch(() => setTenants([]));
  }, [load]);

  const create = async () => {
    if (!form.tenant_id || !form.name.trim()) {
      toast.error("Seleziona il salone e dai un nome alla sorgente");
      return;
    }
    try {
      await api.post("/agency/lead-webhooks", form);
      toast.success("Sorgente creata");
      setCreating(false);
      setForm({ ...form, name: "", landing_name_default: "", form_name_default: "" });
      load();
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Creazione non riuscita");
    }
  };

  const rotate = async (s) => {
    try {
      await api.post(`/agency/lead-webhooks/${s.id}/rotate`);
      toast.success("Secret rigenerato — aggiorna la landing");
      load();
    } catch { toast.error("Rotazione non riuscita"); }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/agency/lead-webhooks/${s.id}`,
        { status: s.status === "active" ? "disabled" : "active" });
      load();
    } catch { toast.error("Aggiornamento non riuscito"); }
  };

  const openDetail = async (s) => {
    if (open === s.id) { setOpen(null); return; }
    setOpen(s.id);
    setEvents([]); setContract(null);
    try {
      const [ev, ct] = await Promise.all([
        api.get(`/agency/lead-webhooks/${s.id}/events?limit=10`),
        api.get(`/agency/lead-webhooks/contract/${s.id}`),
      ]);
      setEvents(ev.data.events || []);
      setContract(ct.data);
    } catch { /* noop */ }
  };

  return (
    <div data-testid="agency-lead-webhooks" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <Webhook className="h-5 w-5" /> Lead Webhooks
          </h1>
          <p className="text-sm text-[color:var(--sp-text-muted)] mt-1">
            Riceve i lead dalle landing e li inserisce nel CRM del salone scelto, con origine e tracking bloccati dal sistema.
          </p>
        </div>
        <button data-testid="new-webhook-source-btn" onClick={() => setCreating((v) => !v)}
          className="btn-primary inline-flex items-center gap-1.5 text-sm">
          <Plus className="h-4 w-4" /> Nuova sorgente
        </button>
      </div>

      {creating && (
        <div data-testid="new-source-form" className="rounded-2xl border border-[color:var(--sp-border)] bg-white p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block text-xs font-bold uppercase tracking-wide text-[color:var(--sp-text-muted)] mb-1">Salone</span>
              <select data-testid="source-tenant-select" value={form.tenant_id}
                onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
                className="w-full rounded-xl border border-[color:var(--sp-border)] px-3 py-2">
                <option value="">Seleziona…</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-xs font-bold uppercase tracking-wide text-[color:var(--sp-text-muted)] mb-1">Nome sorgente</span>
              <input data-testid="source-name-input" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Salon Up Landing Ads"
                className="w-full rounded-xl border border-[color:var(--sp-border)] px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="block text-xs font-bold uppercase tracking-wide text-[color:var(--sp-text-muted)] mb-1">Tipo</span>
              <select data-testid="source-type-select" value={form.source_type}
                onChange={(e) => setForm({ ...form, source_type: e.target.value })}
                className="w-full rounded-xl border border-[color:var(--sp-border)] px-3 py-2">
                {SOURCE_TYPES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-xs font-bold uppercase tracking-wide text-[color:var(--sp-text-muted)] mb-1">Landing (default)</span>
              <input data-testid="source-landing-input" value={form.landing_name_default}
                onChange={(e) => setForm({ ...form, landing_name_default: e.target.value })}
                placeholder="Agency Evergreen"
                className="w-full rounded-xl border border-[color:var(--sp-border)] px-3 py-2" />
            </label>
          </div>
          <div className="flex gap-2">
            <button data-testid="create-source-btn" onClick={create} className="btn-primary text-sm">Crea sorgente</button>
            <button onClick={() => setCreating(false)} className="text-sm px-3 py-2 rounded-xl border border-[color:var(--sp-border)]">Annulla</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-[color:var(--sp-text-muted)]">Caricamento…</p>
      ) : sources.length === 0 ? (
        <p data-testid="no-sources" className="text-sm text-[color:var(--sp-text-muted)]">
          Nessuna sorgente configurata.
        </p>
      ) : (
        <div className="space-y-3">
          {sources.map((s) => (
            <div key={s.id} data-testid={`source-row-${s.id}`}
              className="rounded-2xl border border-[color:var(--sp-border)] bg-white p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold">{s.name}</span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${s.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                      {s.status === "active" ? "Attiva" : "Disattivata"}
                    </span>
                  </div>
                  <p className="text-xs text-[color:var(--sp-text-muted)] mt-0.5">
                    {s.tenant_name} · {SOURCE_TYPES.find((o) => o.v === s.source_type)?.l || s.source_type}
                    {" · "}origine bloccata: {s.acquisition_source}
                  </p>
                  <p className="text-xs text-[color:var(--sp-text-muted)] mt-1">
                    Ultimo lead: <strong>{fmt(s.last_received_at)}</strong> · ok {s.counts?.accepted || 0}
                    {" · "}duplicati {s.counts?.duplicate || 0} · scartati {s.counts?.rejected || 0}
                    {(s.counts?.error || 0) > 0 && <span className="text-rose-600"> · errori {s.counts.error}</span>}
                  </p>
                  {s.last_error && (
                    <p data-testid={`source-error-${s.id}`} className="text-xs text-rose-600 mt-1 inline-flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> {s.last_error} ({fmt(s.last_error_at)})
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`copy-endpoint-${s.id}`} onClick={() => copy(s.endpoint, "Endpoint")}
                    className="text-xs px-2.5 py-1.5 rounded-xl border border-[color:var(--sp-border)] inline-flex items-center gap-1">
                    <Copy className="h-3 w-3" /> Endpoint
                  </button>
                  <button data-testid={`copy-secret-${s.id}`} onClick={() => copy(s.secret, "Secret")}
                    className="text-xs px-2.5 py-1.5 rounded-xl border border-[color:var(--sp-border)] inline-flex items-center gap-1">
                    <Copy className="h-3 w-3" /> Secret
                  </button>
                  <button data-testid={`rotate-secret-${s.id}`} onClick={() => rotate(s)}
                    className="text-xs px-2.5 py-1.5 rounded-xl border border-[color:var(--sp-border)] inline-flex items-center gap-1">
                    <RefreshCw className="h-3 w-3" /> Ruota
                  </button>
                  <button data-testid={`toggle-source-${s.id}`} onClick={() => toggle(s)}
                    className="text-xs px-2.5 py-1.5 rounded-xl border border-[color:var(--sp-border)] inline-flex items-center gap-1">
                    <Power className="h-3 w-3" /> {s.status === "active" ? "Disattiva" : "Attiva"}
                  </button>
                  <button data-testid={`detail-source-${s.id}`} onClick={() => openDetail(s)}
                    className="text-xs px-2.5 py-1.5 rounded-xl border border-[color:var(--sp-border)] inline-flex items-center gap-1">
                    <ChevronRight className={`h-3 w-3 transition-transform ${open === s.id ? "rotate-90" : ""}`} /> Dettagli
                  </button>
                </div>
              </div>

              {open === s.id && (
                <div data-testid={`source-detail-${s.id}`} className="mt-4 pt-4 border-t border-[color:var(--sp-border)] space-y-4">
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-wider text-[color:var(--sp-text-muted)] mb-1 inline-flex items-center gap-1">
                      <Code2 className="h-3 w-3" /> Come collegare la landing
                    </p>
                    <pre className="text-[11px] bg-slate-900 text-slate-100 rounded-xl p-3 overflow-x-auto">
{`POST ${s.endpoint}
Authorization: Bearer ${s.secret}
Content-Type: application/json

${JSON.stringify(contract?.sample_body || {}, null, 2)}`}
                    </pre>
                  </div>
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-wider text-[color:var(--sp-text-muted)] mb-1">Ultimi lead ricevuti</p>
                    {events.length === 0 ? (
                      <p className="text-xs text-[color:var(--sp-text-muted)]">Nessun lead ricevuto.</p>
                    ) : (
                      <ul className="space-y-1 text-xs">
                        {events.map((e) => (
                          <li key={e.id} className="flex items-center justify-between gap-2 border-b border-[color:var(--sp-border)] pb-1">
                            <span>{fmt(e.received_at)}</span>
                            <span className="text-[color:var(--sp-text-muted)] truncate max-w-[50%]">
                              {e.payload_redacted?.email || e.payload_redacted?.phone || "—"}
                            </span>
                            <span className={e.duplicate ? "text-amber-600" : "text-emerald-600"}>
                              {e.duplicate ? "duplicato" : "importato"}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
